/**
 * HTML5 Printable Scan Report Template
 * Generates a clean, professional medical report in HTML5
 */

export const generateScanReportHTML = (reportData, selectedPatient, userInfo, companyInfo) => {
  const dateOfScan = Object.keys(reportData)[0] || '';
  
  // Use company info or defaults
  const company = companyInfo && companyInfo.length > 0 ? companyInfo[0] : {
    name: 'JSB',
    location: 'ABRAFI ESTATE',
    digital_add: 'P.O.BOX',
    phone: '0352290063/0202369814',
    email: '-',
    mmda: 'Quality HealthCare Our Priority'
  };
  
  const scansHTML = Object.keys(reportData)
    .sort((a, b) => new Date(b) - new Date(a))
    .map((date) => {
      return Object.keys(reportData[date])
        .sort()
        .map((scanName) => {
          const results = reportData[date][scanName];
          
          // Separate CONCLUSION from other findings
          const nonConclusionResults = results.filter(r => !r.para || !r.para.toUpperCase().includes('CONCLUSION'));
          const conclusionResults = results.filter(r => r.para && r.para.toUpperCase().includes('CONCLUSION'));
          const sortedResults = [...nonConclusionResults, ...conclusionResults];
          
          const parametersHTML = sortedResults
            .map(result => `
              <tr>
                <td>${result.para}</td>
                <td>${result.finding}</td>
                <td>${result.flag || '-'}</td>
              </tr>
            `)
            .join('');
          
          return `
            <div class="scan-section">
              <div class="scan-title">${scanName.toUpperCase()}</div>
              
              <table>
                <thead>
                  <tr>
                    <th>Parameter</th>
                    <th>Finding</th>
                    <th>Flag</th>
                  </tr>
                </thead>
                <tbody>
                  ${parametersHTML}
                </tbody>
              </table>
            </div>
          `;
        })
        .join('');
    })
    .join('');

  const html = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Scan Report - ${selectedPatient?.fullName || 'Patient'}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        html, body {
          width: 100%;
          font-family: 'Segoe UI', 'Trebuchet MS', 'Arial', sans-serif;
          font-size: 10pt;
          line-height: 1.5;
          color: #555;
        }

        body {
          padding: 0 !important;
          margin: 0 !important;
          background: #f5f5f5;
          min-height: 100vh;
        }

        .screen-only {
          display: block;
          padding: 20px;
          background-color: white;
        }

        .button-container {
          text-align: center;
          margin-bottom: 20px;
          padding: 15px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        button {
          padding: 10px 24px;
          margin: 0 8px;
          font-size: 11pt;
          cursor: pointer;
          background: #333;
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          transition: all 0.3s ease;
          box-shadow: 0 4px 8px rgba(0,0,0,0.2);
        }

        button:hover {
          transform: translateY(-2px);
          background: #1a1a1a;
          box-shadow: 0 6px 12px rgba(0,0,0,0.3);
        }

        button.close-btn {
          background: #666;
        }

        button.close-btn:hover {
          background: #555;
        }

        .print-container {
          width: 210mm;
          margin: 0;
          padding: 12mm 12mm 12mm 12mm;
          background-color: white;
          page-break-after: always;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }

        .header {
          text-align: center;
          margin-bottom: 8px;
          padding-bottom: 6px;
          border-bottom: 1px solid #999;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 15px;
        }

        .header-left {
          flex: 1;
          text-align: left;
          font-size: 9pt;
          line-height: 1.6;
        }

        .header-left strong {
          color: #666;
          font-weight: 600;
          display: inline-block;
          min-width: 70px;
        }

        .logo-container {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .logo-container img {
          max-width: 80px;
          max-height: 80px;
          object-fit: contain;
        }

        .header-right {
          flex: 1;
          text-align: left;
          font-size: 9pt;
          line-height: 1.6;
        }

        .header-right strong {
          color: #666;
          font-weight: 600;
          display: inline-block;
          min-width: 75px;
        }

        .hospital-name {
          font-size: 20pt;
          font-weight: 700;
          margin-bottom: 4px;
          color: #333;
          letter-spacing: 1px;
          text-align: center;
        }

        .report-title {
          text-align: center;
          font-size: 14pt;
          font-weight: 600;
          color: #555;
          margin: 8px 0 6px 0;
          padding: 4px 0;
          border-bottom: 1px solid #ccc;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .scan-date {
          margin: 4px 0;
          font-weight: 600;
          font-size: 10pt;
          color: #555;
          padding: 4px 6px;
          background: #f5f5f5;
          border-left: 3px solid #999;
          border-radius: 4px;
        }

        .patient-info {
          display: flex;
          flex-wrap: wrap;
          gap: 8px 12px;
          margin: 4px 0 3px 0;
          padding: 4px 3px;
          background: #fafafa;
          border-left: 3px solid #999;
          border-radius: 4px;
          font-size: 9pt;
          line-height: 1.4;
          justify-content: flex-start;
          align-items: center;
        }

        .patient-info div {
          display: flex;
          align-items: center;
          gap: 6px;
          min-width: 120px;
          flex: 0 0 auto;
        }

        .patient-info strong {
          color: #666;
          font-weight: 600;
          min-width: 55px;
        }

        .content {
          margin-top: 6px;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 6px;
          page-break-inside: avoid;
          box-shadow: 0 2px 6px rgba(0,0,0,0.08);
          border-radius: 4px;
          overflow: hidden;
          font-size: 9.5pt;
        }

        th, td {
          padding: 10px;
          text-align: left;
        }

        th {
          background: #666;
          color: white;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          font-size: 9pt;
        }

        td {
          border-bottom: 1px solid #ccc;
          color: #555;
        }

        tbody tr:nth-child(even) {
          background-color: #f9f9f9;
        }

        tbody tr:hover {
          background-color: #f0f0f0;
        }

        .scan-section {
          margin-bottom: 6px;
          page-break-inside: avoid;
          padding-bottom: 2px;
        }

        .scan-title {
          text-align: center;
          font-size: 11pt;
          font-weight: 700;
          color: #333;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 12px;
          padding: 8px 0;
          border-bottom: 1px solid #999;
        }

        .footer {
          margin-top: 12px;
          padding-top: 6px;
          border-top: 1px solid #999;
          font-size: 9pt;
          color: #666;
          font-weight: 500;
        }

        .footer strong {
          color: #555;
        }

        @media print {
          body {
            padding: 0;
            background: white;
            margin: 0;
          }

          .screen-only {
            display: none !important;
          }

          .print-container {
            width: 210mm;
            height: auto;
            margin: 0;
            padding: 8mm 8mm 15mm 8mm;
            box-shadow: none;
            page-break-after: always;
          }

          table {
            page-break-inside: avoid;
            box-shadow: none;
          }

          .scan-section {
            page-break-inside: avoid;
          }
        }
      </style>
    </head>
    <body>
      <div class="screen-only">
        <div class="button-container">
          <button onclick="window.print()">Print / Save as PDF</button>
          <button class="close-btn" onclick="window.close()">Close</button>
        </div>
      </div>

      <div class="print-container">
        <!-- Hospital Header -->
        <div class="hospital-name">${company.name || 'Hospital Name'}</div>
        <div class="header">
          <div class="header-left">
            <strong>Address:</strong> ${company.digital_add || 'N/A'}<br>
            <strong>Location:</strong> ${company.location || 'N/A'}<br>
            <strong>Mission:</strong> ${company.mmda || 'Quality Healthcare'}
          </div>
            <div class="logo-container">
            ${company.logo 
              ? `<img src="${company.logo}" alt="Hospital Logo" style="max-width:80px; max-height:80px; object-fit:contain;">`
              : ''
            }
          </div>
          <div class="header-right">
            <strong>Telephone:</strong> ${company.phone || 'N/A'}<br>
            <strong>Email:</strong> ${company.email || 'N/A'}<br>
            <strong>Website:</strong> ${company.website || 'N/A'}
          </div>
        </div>

        <!-- Scan Date -->
        <div class="scan-date">
          <strong>Date Of Scan:</strong> ${dateOfScan}
        </div>

        <!-- Patient Information -->
        <div class="patient-info">
          <div>
            <strong>Patient ID:</strong> ${selectedPatient?.id || 'N/A'}
          </div>
          <div>
            <strong>Name:</strong> ${selectedPatient?.fullName || 'N/A'}
          </div>
          <div>
            <strong>Gender:</strong> ${selectedPatient?.gender || 'N/A'}
          </div>
          <div>
            <strong>Age:</strong> ${selectedPatient?.age || 'N/A'}
          </div>
          <div>
            <strong>Tel #:</strong> ${selectedPatient?.tel || 'N/A'}
          </div>
        </div>

        <!-- Report Title -->
        <div class="report-title">
          SCAN RESULTS
        </div>

        <!-- Content -->
        <div class="content">
          ${scansHTML}
        </div>

        <!-- Footer -->
        <div class="footer">
          <strong>Scanned by:</strong> ${userInfo?.user_name || userInfo?.name || 'Not specified'}
        </div>
      </div>
    </body>
    </html>
  `;

  return html;
};
